// src/components/OperatorDashboard/OperatorDashboard.js
import React, { useState, useEffect } from 'react';
import './OperatorDashboard.css';

// Import Cloudflare Images URLs for demo chefs - 10 distinct, gender-balanced chef images from Unsplash
const CHEF1 = "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=400&fit=crop&crop=face"; // Female chef
const CHEF2 = "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=400&fit=crop&crop=face"; // Male chef
const CHEF3 = "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=400&h=400&fit=crop&crop=face"; // Female chef
const CHEF4 = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face"; // Male chef
const CHEF5 = "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face"; // Female chef
const CHEF6 = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face"; // Male chef
const CHEF7 = "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face"; // Female chef
const CHEF8 = "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face"; // Male chef
const CHEF9 = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop&crop=face"; // Female chef
const CHEF10 = "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=face"; // Male chef

const CITIES = [
  { id: 'all', name: 'All Cities', operator: 'operator-all' },
  { id: 'toronto', name: 'Toronto', operator: 'operator-toronto' },
  { id: 'boston', name: 'Boston', operator: 'operator-boston' },
  { id: 'new-york', name: 'New York', operator: 'operator-ny' },
  { id: 'chicago', name: 'Chicago', operator: 'operator-chicago' },
  { id: 'new-orleans', name: 'New Orleans', operator: 'operator-nola' },
  { id: 'miami', name: 'Miami', operator: 'operator-miami' },
  { id: 'austin', name: 'Austin', operator: 'operator-austin' }
];

export default function OperatorDashboard() {
  const [selectedCity, setSelectedCity] = useState('toronto');
  const [chefs, setChefs] = useState([]);
  const [pendingChefs, setPendingChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operatorRole, setOperatorRole] = useState(null);
  const [activeTab, setActiveTab] = useState('approved'); // 'approved' or 'pending'
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showLogin, setShowLogin] = useState(true);
  const [editingChef, setEditingChef] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [specialtiesInput, setSpecialtiesInput] = useState('');
  const [chefNameInput, setChefNameInput] = useState('');
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [imageSearchResults, setImageSearchResults] = useState([]);
  const [searchingImages, setSearchingImages] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated
    const savedAuth = localStorage.getItem('operator_authenticated');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
      setShowLogin(false);
      loadChefsForCity(selectedCity);
      loadPendingChefsForCity(selectedCity);
      checkOperatorRole();
    } else {
      setLoading(false);
    }
  }, [selectedCity]);

  const checkOperatorRole = () => {
    // TODO: Implement role-based authentication
    const role = localStorage.getItem('operator_role') || 'operator-toronto';
    setOperatorRole(role);
  };

  const handleLogin = () => {
    // Simple password check - you can change this password
    if (password === 'Boodgy') {
      setIsAuthenticated(true);
      setShowLogin(false);
      localStorage.setItem('operator_authenticated', 'true');
      localStorage.setItem('admin_token', 'Boodgy'); // Set admin token for API calls
      loadChefsForCity(selectedCity);
      loadPendingChefsForCity(selectedCity);
      checkOperatorRole();
    } else {
      alert('Invalid password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setShowLogin(true);
    setPassword('');
    localStorage.removeItem('operator_authenticated');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('operator_token');
  };

  // Safe JSON parsing for specialties
  const parseSpecialties = (specialties) => {
    if (!specialties) return [];
    try {
      const parsed = JSON.parse(specialties);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // If it's not valid JSON, treat it as a single string
      return [specialties];
    }
  };

  // REAL Unsplash API search
  const searchUnsplashImages = async (query) => {
    if (!query.trim()) return;
    
    setSearchingImages(true);
    console.log(`🔍 REAL UNSPLASH API SEARCH FOR: "${query}"`);
    
    try {
      // Use Unsplash API - free tier allows 50 requests/hour
      const UNSPLASH_ACCESS_KEY = 'pKmNeXH5IH9qPRjGZ8aqVPRjy9f5H8b6K_PkOqJEh_8';
      const response = await fetch(
        `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=20&orientation=squarish`,
        {
          headers: {
            'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Unsplash API error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`✅ UNSPLASH RETURNED ${data.results.length} REAL IMAGES`);
      
      if (data.results && data.results.length > 0) {
        const images = data.results.map(img => ({
          id: img.id,
          urls: { 
            regular: img.urls.small // Use small size for faster loading
          },
          alt_description: img.alt_description || img.description || query
        }));
        setImageSearchResults(images);
      } else {
        console.warn('No results from Unsplash');
        setImageSearchResults([]);
      }
    } catch (error) {
      console.error('❌ Unsplash API error:', error);
      alert(`Unsplash search failed: ${error.message}`);
      setImageSearchResults([]);
    } finally {
      setSearchingImages(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('📁 File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be smaller than 10MB');
      return;
    }

    setUploadingImage(true);
    try {
      console.log('🚀 Starting upload to Cloudflare Images...');
      
      const adminToken = localStorage.getItem('admin_token') || 'Boodgy';
      console.log('🔑 Using admin token:', adminToken);

      // Step 1: Get upload URL (include admin token for auth)
      const urlResponse = await fetch('/api/images/direct-upload', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          metadata: {
            chef_id: editingChef?.id || 'unknown',
            chef_name: editingChef?.name || 'Unknown',
            uploaded_by: 'operator',
            upload_date: new Date().toISOString()
          }
        })
      });

      console.log('📥 Upload URL response status:', urlResponse.status);

      if (!urlResponse.ok) {
        const errorText = await urlResponse.text();
        console.error('❌ Upload URL error:', errorText);
        throw new Error(`Failed to get upload URL: ${urlResponse.status} - ${errorText}`);
      }

      const { uploadURL } = await urlResponse.json();
      console.log('✅ Got upload URL');

      // Step 2: Upload image to Cloudflare
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(uploadURL, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      const uploadResult = await uploadResponse.json();
      console.log('✅ Upload result:', uploadResult);

      // Construct Cloudflare Images URL
      const imageUrl = `https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/${uploadResult.result.id}/public`;
      console.log('✅ Image URL:', imageUrl);

      setSelectedImageUrl(imageUrl);
      alert('Image uploaded! Click "Save Changes" to apply it to the chef.');
    } catch (error) {
      console.error('❌ Upload error:', error);
      alert(`Failed to upload image: ${error.message}`);
    } finally {
      setUploadingImage(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  // Handle image selection (just save it, don't update yet)
  const handleImageSelect = (imageUrl) => {
    console.log('Image selected:', imageUrl);
    setSelectedImageUrl(imageUrl);
    setShowImageSearch(false);
    setImageSearchResults([]);
    setImageSearchQuery('');
    alert('Image selected! Click "Save Changes" to apply it.');
  };

  // Handle image search
  const handleImageSearch = () => {
    console.log('🔍 Search button clicked, query:', imageSearchQuery);
    if (imageSearchQuery.trim()) {
      console.log('✅ Calling searchUnsplashImages with:', imageSearchQuery);
      searchUnsplashImages(imageSearchQuery);
    } else {
      console.log('⚠️ Search query is empty');
    }
  };

  const loadChefsForCity = async (city) => {
    try {
      setLoading(true);
      
      if (city === 'all') {
        // Load chefs from all cities
        const cityIds = CITIES.filter(c => c.id !== 'all').map(c => c.id);
        const allChefs = [];
        
        for (const cityId of cityIds) {
          try {
            const response = await fetch(`/api/chefs?city=${cityId}&status=approved,live`);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            
            if (data.results && Array.isArray(data.results)) {
              // Add city info to each chef
              const chefsWithCity = data.results.map(chef => ({
                ...chef,
                city_name: CITIES.find(c => c.id === cityId)?.name || cityId,
                city_id: cityId
              }));
              allChefs.push(...chefsWithCity);
            }
          } catch (error) {
            console.error(`Error loading chefs for ${cityId}:`, error);
          }
        }
        
        setChefs(allChefs);
      } else {
        // Load chefs for specific city
        const response = await fetch(`/api/chefs?city=${city}&status=approved,live`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setChefs(data.results || []);
      }
    } catch (error) {
      console.error('Error loading chefs:', error);
      setChefs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingChefsForCity = async (city) => {
    try {
      if (city === 'all') {
        // Load pending chefs from all cities
        const cityIds = CITIES.filter(c => c.id !== 'all').map(c => c.id);
        const allPendingChefs = [];
        
        for (const cityId of cityIds) {
          try {
            const response = await fetch(`/api/chefs?city=${cityId}&status=pending`);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.results && Array.isArray(data.results)) {
              // Add city info to each chef
              const chefsWithCity = data.results.map(chef => ({
                ...chef,
                city_name: CITIES.find(c => c.id === cityId)?.name || cityId,
                city_id: cityId
              }));
              allPendingChefs.push(...chefsWithCity);
            }
          } catch (error) {
            console.error(`Error loading pending chefs for ${cityId}:`, error);
          }
        }
        
        setPendingChefs(allPendingChefs);
      } else {
        // Load pending chefs for specific city
        const response = await fetch(`/api/chefs?city=${city}&status=pending`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setPendingChefs(data.results || []);
      }
    } catch (error) {
      console.error('Error loading pending chefs:', error);
      setPendingChefs([]);
    }
  };

  const handleChefStatusChange = async (chefId, newStatus) => {
    try {
      const response = await fetch('/api/operator/chef-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('operator_token')}`
        },
        body: JSON.stringify({
          chefId,
          status: newStatus,
          city: selectedCity
        })
      });

      if (response.ok) {
        // Reload chefs for the city
        loadChefsForCity(selectedCity);
      }
    } catch (error) {
      console.error('Error updating chef status:', error);
    }
  };

  const handleChefApproval = async (chefId, action) => {
    try {
      const response = await fetch('/api/admin/chefs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || 'Boodgy'}`
        },
        body: JSON.stringify({
          id: chefId,
          action: action // 'approve' or 'reject'
        })
      });

      if (response.ok) {
        // Reload both approved and pending chefs
        loadChefsForCity(selectedCity);
        loadPendingChefsForCity(selectedCity);
        alert(`Chef ${action}d successfully!`);
      } else {
        alert(`Error ${action}ing chef. Please try again.`);
      }
    } catch (error) {
      console.error(`Error ${action}ing chef:`, error);
      alert(`Error ${action}ing chef. Please try again.`);
    }
  };

  const handleEditSpecialties = (chef) => {
    console.log('🖼️ Opening edit modal for chef:', chef.name);
    setEditingChef(chef);
    const specialties = parseSpecialties(chef.specialties).join(', ');
    setSpecialtiesInput(specialties);
    setChefNameInput(chef.name || '');
    setSelectedImageUrl(null); // Reset any previously selected image
    setShowImageSearch(false);
    setImageSearchResults([]);
    setImageSearchQuery('professional chef'); // Set default search query
    setShowEditModal(true);
  };

  const handleSaveSpecialties = async () => {
    if (!editingChef) return;

    try {
      // Parse specialties from comma-separated input
      const specialties = specialtiesInput
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      // Prepare update data
      const updateData = {
        chef_id: editingChef.id,
        specialties: JSON.stringify(specialties)
      };

      // Add name update if it changed
      if (chefNameInput.trim() !== editingChef.name) {
        updateData.name = chefNameInput.trim();
      }

      // Add photo_url update if a new image was selected
      if (selectedImageUrl) {
        updateData.photo_url = selectedImageUrl;
        console.log('✅ NEW IMAGE URL TO SAVE:', selectedImageUrl);
      } else {
        console.log('⚠️ NO NEW IMAGE SELECTED - selectedImageUrl is:', selectedImageUrl);
      }

      console.log('📤 SENDING TO API:', JSON.stringify(updateData, null, 2));
      console.log('📤 API URL:', '/api/admin/chefs');
      console.log('📤 METHOD:', 'PUT');
      console.log('📤 TOKEN:', localStorage.getItem('admin_token') || 'Boodgy');

      const response = await fetch('/api/admin/chefs', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || 'Boodgy'}`
        },
        body: JSON.stringify(updateData)
      });

      console.log('📥 API RESPONSE STATUS:', response.status);
      console.log('📥 API RESPONSE OK:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ API SUCCESS RESPONSE:', result);
        alert('Chef profile updated successfully! Check the chef card to see the new image.');
        setShowEditModal(false);
        setEditingChef(null);
        setSpecialtiesInput('');
        setChefNameInput('');
        setSelectedImageUrl(null);
        // Reload chefs to show updated data
        await loadChefsForCity(selectedCity);
        console.log('🔄 Chefs reloaded after save');
      } else {
        const errorText = await response.text();
        console.error('❌ API ERROR RESPONSE:', errorText);
        alert(`Error updating chef profile: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating chef profile:', error);
      alert('Error updating chef profile. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingChef(null);
    setSpecialtiesInput('');
    setChefNameInput('');
    setShowImageSearch(false);
    setImageSearchQuery('');
    setImageSearchResults([]);
    setSelectedImageUrl(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'available': { text: 'Available', class: 'status-available' },
      'busy': { text: 'Busy', class: 'status-busy' },
      'offline': { text: 'Offline', class: 'status-offline' },
      'checking-in': { text: 'Checking In', class: 'status-checking-in' },
      'checking-out': { text: 'Checking Out', class: 'status-checking-out' }
    };
    const badge = badges[status] || { text: status, class: 'status-unknown' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const getMealAvailability = (chef) => {
    // TODO: Implement meal availability logic
    return chef.meals_available || 0;
  };

  const getChefImage = (chef) => {
    // If chef has a real photo, use it
    if (chef.photo_url) {
      return chef.photo_url;
    }
    
    // Otherwise, assign a demo image based on chef name hash
    const demoImages = [CHEF1, CHEF2, CHEF3, CHEF4, CHEF5, CHEF6, CHEF7, CHEF8, CHEF9, CHEF10];
    
    // Create a simple hash from chef name to ensure consistent image assignment
    let hash = 0;
    const chefName = chef.name || `chef-${chef.id}`;
    for (let i = 0; i < chefName.length; i++) {
      hash = (hash * 31 + chefName.charCodeAt(i)) >>> 0;
    }
    
    return demoImages[hash % demoImages.length];
  };

  if (loading) {
    return (
      <div className="operator-dashboard-container">
        <div className="loading">Loading operator panel...</div>
      </div>
    );
  }

  if (showLogin || !isAuthenticated) {
    return (
      <div className="operator-login-container">
        <div className="login-box">
          <h1>🔒 Operator Access</h1>
          <p>Enter password to access the operator dashboard:</p>
          <input
            type="password"
            placeholder="Enter operator password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            className="password-input"
          />
          <button onClick={handleLogin} className="login-btn">
            Access Dashboard
          </button>
          <div className="login-note">
            <small>Contact admin for access credentials</small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="operator-dashboard">
      <div className="dashboard-header">
        <h1>Operator Control Panel</h1>
        <div className="operator-info">
          <span className="operator-role">{operatorRole}</span>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div className="city-selector">
        <h2>Select City to Manage:</h2>
        <div className="city-buttons">
          {CITIES.map(city => (
            <button
              key={city.id}
              className={`city-btn ${selectedCity === city.id ? 'active' : ''}`}
              onClick={() => setSelectedCity(city.id)}
            >
              {city.name}
            </button>
          ))}
        </div>
      </div>

      <div className="city-dashboard">
        <div className="city-header">
          <h2>{selectedCity === 'all' ? 'All Cities' : CITIES.find(c => c.id === selectedCity)?.name} Operations</h2>
          <div className="city-stats">
            <div className="stat">
              <span className="stat-number">{chefs.length}</span>
              <span className="stat-label">Approved Chefs</span>
            </div>
            <div className="stat">
              <span className="stat-number">{pendingChefs.length}</span>
              <span className="stat-label">Pending Review</span>
            </div>
            <div className="stat">
              <span className="stat-number">{chefs.filter(c => c.status === 'available').length}</span>
              <span className="stat-label">Available</span>
            </div>
          </div>
        </div>

        <div className="tab-system">
          <button 
            className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            Approved Chefs ({chefs.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending Review ({pendingChefs.length})
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading chefs...</div>
        ) : (
          <div className="chefs-grid">
            {activeTab === 'approved' ? (
              // Approved chefs - show status controls
              chefs.map(chef => (
                <div key={chef.id} className="chef-control-card">
                  <div className="chef-photo">
                    <img 
                      src={getChefImage(chef)} 
                      alt={chef.name}
                      className="chef-image"
                    />
                  </div>
                  
                  <div className="chef-info-section">
                    <div className="chef-info">
                      <h3 className="chef-name">
                        {chef.name}
                        {selectedCity === 'all' && chef.city_name && (
                          <span className="chef-city-badge">({chef.city_name})</span>
                        )}
                      </h3>
                      <div className="chef-specialties">
                        {parseSpecialties(chef.specialties).map((specialty, index) => (
                          <span key={index} className="specialty-tag">{specialty}</span>
                        ))}
                      </div>
                      <div className="chef-status">
                        {getStatusBadge(chef.status || 'offline')}
                      </div>
                      <div className="meal-info">
                        <span className="meal-count">{getMealAvailability(chef)} meals available</span>
                      </div>
                    </div>

                    <div className="chef-controls">
                      <div className="status-controls">
                        <button 
                          className={`status-btn ${chef.status === 'available' ? 'active' : ''}`}
                          onClick={() => handleChefStatusChange(chef.id, 'available')}
                        >
                          ✅ Available
                        </button>
                        <button 
                          className={`status-btn ${chef.status === 'busy' ? 'active' : ''}`}
                          onClick={() => handleChefStatusChange(chef.id, 'busy')}
                        >
                          ⏰ Busy
                        </button>
                        <button 
                          className={`status-btn ${chef.status === 'offline' ? 'active' : ''}`}
                          onClick={() => handleChefStatusChange(chef.id, 'offline')}
                        >
                          ❌ Offline
                        </button>
                      </div>
                      
                      <div className="action-controls">
                        <button className="action-btn checkin-btn">
                          📝 Check In
                        </button>
                        <button className="action-btn checkout-btn">
                          📤 Check Out
                        </button>
                        <button className="action-btn meals-btn">
                          🍽️ Manage Meals
                        </button>
                        <button 
                          className="action-btn edit-btn"
                          onClick={() => handleEditSpecialties(chef)}
                        >
                          ✏️ Edit Profile
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // Pending chefs - show approval controls
              pendingChefs.map(chef => (
                <div key={chef.id} className="chef-control-card pending-card">
                  <div className="chef-photo">
                    <img 
                      src={getChefImage(chef)} 
                      alt={chef.name}
                      className="chef-image"
                    />
                  </div>
                  
                  <div className="chef-info-section">
                    <div className="chef-info">
                      <h3 className="chef-name">
                        {chef.name}
                        {selectedCity === 'all' && chef.city_name && (
                          <span className="chef-city-badge">({chef.city_name})</span>
                        )}
                      </h3>
                      <div className="chef-status">
                        <span className="status-badge status-pending">Pending Review</span>
                      </div>
                      <div className="chef-details">
                        <div className="detail-item">
                          <strong>Email:</strong> {chef.email || 'Not provided'}
                        </div>
                        <div className="detail-item">
                          <strong>Applied:</strong> {new Date(chef.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    <div className="chef-controls">
                      <div className="approval-controls">
                        <button 
                          className="action-btn approve-btn"
                          onClick={() => handleChefApproval(chef.id, 'approve')}
                        >
                          ✅ Approve Chef
                        </button>
                        <button 
                          className="action-btn reject-btn"
                          onClick={() => handleChefApproval(chef.id, 'reject')}
                        >
                          ❌ Reject Chef
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="system-status">
        <h3>System Status</h3>
        <div className="status-indicators">
          <div className="indicator online">
            <span className="indicator-dot"></span>
            <span>System Online</span>
          </div>
          <div className="indicator">
            <span className="indicator-dot"></span>
            <span>Last Update: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* Edit Specialties Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Chef Profile - {editingChef?.name}</h3>
              <button className="modal-close" onClick={handleCancelEdit}>×</button>
            </div>
            <div className="modal-body">
              {/* Current Image Display */}
              <div className="form-group">
                <label>{selectedImageUrl ? 'New Image Selected:' : 'Current Image:'}</label>
                <div className="current-image-container">
                  <img 
                    src={selectedImageUrl || editingChef?.photo_url} 
                    alt={editingChef?.name}
                    className="current-chef-image"
                    style={selectedImageUrl ? { border: '3px solid #4caf50' } : {}}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* File Upload Button */}
                    <label 
                      className="btn-upload-image"
                      style={{ 
                        padding: '8px 16px', 
                        background: uploadingImage ? '#ccc' : '#4caf50', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: uploadingImage ? 'not-allowed' : 'pointer',
                        textAlign: 'center',
                        display: 'block'
                      }}
                    >
                      {uploadingImage ? '⏳ Uploading...' : '📤 Upload Image'}
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={handleFileUpload}
                        disabled={uploadingImage}
                        style={{ display: 'none' }}
                      />
                    </label>

                    <button 
                      className="btn-search-images"
                      onClick={() => {
                        console.log('🔍 Toggle image search, currently:', showImageSearch);
                        if (!showImageSearch && imageSearchResults.length === 0) {
                          console.log('🚀 Auto-searching with default query');
                          searchUnsplashImages('professional chef');
                        }
                        setShowImageSearch(!showImageSearch);
                      }}
                      disabled={uploadingImage}
                    >
                      🔍 {selectedImageUrl ? 'Choose Different Image' : 'Search Images'}
                    </button>
                    
                    {selectedImageUrl && (
                      <button 
                        className="btn-clear-selection"
                        onClick={() => setSelectedImageUrl(null)}
                        style={{ padding: '8px 16px', background: '#f44336', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        ✖ Clear Selection
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Image Search Section */}
              {showImageSearch && (
                <div className="image-search-section">
                  <div className="form-group">
                    <label htmlFor="image-search">Search for chef images:</label>
                    <div className="search-input-group">
                      <input
                        id="image-search"
                        type="text"
                        value={imageSearchQuery}
                        onChange={(e) => setImageSearchQuery(e.target.value)}
                        placeholder="e.g., professional chef, chef portrait, male chef"
                        className="image-search-input"
                        onKeyPress={(e) => e.key === 'Enter' && handleImageSearch()}
                      />
                      <button 
                        className="btn-search"
                        onClick={handleImageSearch}
                        disabled={searchingImages}
                      >
                        {searchingImages ? 'Searching...' : 'Search'}
                      </button>
                    </div>
                  </div>

                  {/* Image Results */}
                  {imageSearchResults.length > 0 && (
                    <div className="image-results">
                      <h4>Select a new image:</h4>
                      <div className="image-grid">
                        {imageSearchResults.map((image) => (
                          <div 
                            key={image.id} 
                            className="image-option"
                            onClick={() => handleImageSelect(image.urls.regular)}
                          >
                            <img 
                              src={image.urls.regular} 
                              alt={image.alt_description || 'Chef image'}
                              className="search-result-image"
                            />
                            <div className="image-overlay">
                              <span>Select</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="chef-name">Chef Name:</label>
                <input
                  id="chef-name"
                  type="text"
                  value={chefNameInput}
                  onChange={(e) => setChefNameInput(e.target.value)}
                  placeholder="Enter chef name"
                  className="chef-name-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="specialties">Specialties (separated by commas):</label>
                <input
                  id="specialties"
                  type="text"
                  value={specialtiesInput}
                  onChange={(e) => setSpecialtiesInput(e.target.value)}
                  placeholder="e.g., BBQ, Smoking, Grilling"
                  className="specialties-input"
                />
              </div>
              
              <div className="current-specialties">
                <strong>Current specialties:</strong>
                {parseSpecialties(editingChef?.specialties).map((specialty, index) => (
                  <span key={index} className="specialty-tag">{specialty}</span>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCancelEdit}>Cancel</button>
              <button className="btn-save" onClick={handleSaveSpecialties}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
